import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/use-auth';
import { ApiError } from '@/lib/api';
import { getMyEarnings, requestPayout, getMyPayouts } from '@/lib/earning-api';
import { fetchMyTutorProfile } from '@/lib/tutor-api';
import type { TutorEarningResponse, PayoutRecordResponse } from '@/lib/schemas/earning';
import type { TutorProfileResponse } from '@/lib/schemas/tutor-profile';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Hourglass,
  Wallet,
  CheckCircle2,
  Coins,
  History,
  FileText,
  ShieldAlert,
  ArrowUpRight,
} from 'lucide-react';

const EARNING_COLORS: Record<string, string> = {
  held: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900/50',
  eligible: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900/50',
  released: 'bg-slate-100 text-slate-650 border-slate-200 dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700',
};

const PAYOUT_COLORS: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900/50',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900/50',
  rejected: 'bg-red-50 text-red-750 border-red-200 dark:bg-red-950/30 dark:text-red-400 dark:border-red-900/50',
};

function formatCurrency(minor: number, currency: string): string {
  const symbol = currency === 'USD' ? '$' : '₹';
  const amount = minor / 100;
  return `${symbol}${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function EarningsSkeleton() {
  return (
    <div className="w-full max-w-3xl space-y-6 py-2">
      {/* Header Skeleton */}
      <div className="space-y-2">
        <Skeleton className="h-7 w-32 rounded" />
        <Skeleton className="h-4 w-80" />
      </div>

      {/* Stats Cards Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 p-5 flex items-start justify-between gap-3">
            <div className="space-y-2.5 flex-1 min-w-0">
              <Skeleton className="h-3.5 w-16 rounded-full" />
              <Skeleton className="h-8 w-24 rounded-lg" />
              <Skeleton className="h-3 w-36 rounded-full" />
            </div>
            <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
          </div>
        ))}
      </div>

      {/* Payout Action Card Skeleton */}
      <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-2 flex-1">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded-full" />
            <Skeleton className="h-5 w-32 rounded-md" />
          </div>
          <Skeleton className="h-4 w-5/6 rounded-md" />
          <Skeleton className="h-4 w-4/6 rounded-md" />
        </div>
        <Skeleton className="h-10 w-32 rounded-md shrink-0" />
      </div>

      {/* Table Skeleton */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded-full" />
          <Skeleton className="h-5 w-36 rounded-md" />
        </div>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 shadow-sm">
          {/* Mock table rows */}
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            <div className="bg-slate-50 dark:bg-slate-900/45 p-4 grid grid-cols-6 gap-4">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-3 w-16 rounded-md" />
              ))}
            </div>
            {[...Array(3)].map((_, idx) => (
              <div key={idx} className="p-4 grid grid-cols-6 gap-4 items-center">
                <Skeleton className="h-4 w-20 rounded-md" />
                <Skeleton className="h-4 w-12 rounded-md justify-self-end" />
                <Skeleton className="h-4 w-12 rounded-md justify-self-end" />
                <Skeleton className="h-4 w-12 rounded-md justify-self-end" />
                <Skeleton className="h-5 w-16 rounded-full justify-self-center" />
                <Skeleton className="h-4 w-20 rounded-md" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Payout History Skeleton */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded-full" />
          <Skeleton className="h-5 w-32 rounded-md" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 p-5 shadow-sm border-l-4 border-l-slate-200 dark:border-l-slate-700 flex items-center justify-between gap-4">
              <div className="space-y-2 flex-1">
                <Skeleton className="h-6 w-20 rounded-md" />
                <Skeleton className="h-3 w-28 rounded-md" />
              </div>
              <Skeleton className="h-5 w-16 rounded-full shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function EarningsPage() {
  const { accessToken } = useAuth();
  const [earnings, setEarnings] = useState<TutorEarningResponse[]>([]);
  const [payouts, setPayouts] = useState<PayoutRecordResponse[]>([]);
  const [profile, setProfile] = useState<TutorProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [payoutError, setPayoutError] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setLoading(true);

    Promise.all([
      getMyEarnings(accessToken),
      getMyPayouts(accessToken),
      fetchMyTutorProfile(accessToken).catch((err) => {
        console.error('Failed to fetch tutor profile', err);
        return null;
      }),
    ])
      .then(([e, p, prof]) => {
        if (!cancelled) {
          setEarnings(e);
          setPayouts(p);
          setProfile(prof);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load earnings information');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const heldTotal = earnings.filter((e) => e.status === 'held').reduce((s, e) => s + e.netMinor, 0);
  const eligibleTotal = earnings.filter((e) => e.status === 'eligible').reduce((s, e) => s + e.netMinor, 0);
  const releasedTotal = earnings.filter((e) => e.status === 'released').reduce((s, e) => s + e.netMinor, 0);
  const hasEligible = eligibleTotal > 0;

  const payoutCurrency = profile?.payoutCurrency || earnings[0]?.currency || payouts[0]?.currency || 'INR';

  async function handleRequestPayout() {
    if (!accessToken) return;
    setPayoutBusy(true);
    setPayoutError(null);
    try {
      const record = await requestPayout(accessToken);
      setPayouts((prev) => [record, ...prev]);
      setEarnings((prev) =>
        prev.map((e) => (e.status === 'eligible' ? { ...e, status: 'released' as const } : e)),
      );
    } catch (e) {
      setPayoutError(e instanceof ApiError ? e.message : 'Payout request failed');
    } finally {
      setPayoutBusy(false);
    }
  }

  if (loading) {
    return <EarningsSkeleton />;
  }

  return (
    <div className="w-full max-w-3xl space-y-6 py-2 animate-fade-in">
      {/* Header */}
      <div className="space-y-1 shrink-0">
        <h1 className="text-lg md:text-xl font-semibold text-slate-900 dark:text-slate-100">
          My Earnings
        </h1>
        <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400">
          Monitor your classroom earnings, requested payouts, and pending balances.
        </p>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50/70 p-4 text-sm font-semibold text-red-750 dark:border-red-900/50 dark:bg-red-950/20 backdrop-blur-md flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 shrink-0 text-red-500" />
          <span>{error}</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          {
            label: 'Held Balance',
            value: formatCurrency(heldTotal, payoutCurrency),
            description: 'Pending slot validation & review',
            icon: <Hourglass className="h-5 w-5 text-slate-450 dark:text-slate-400" />,
          },
          {
            label: 'Available for Payout',
            value: formatCurrency(eligibleTotal, payoutCurrency),
            description: 'Eligible for immediate withdrawal',
            icon: <Wallet className="h-5 w-5 text-slate-450 dark:text-slate-400" />,
          },
          {
            label: 'Paid Out',
            value: formatCurrency(releasedTotal, payoutCurrency),
            description: 'Successfully transferred payouts',
            icon: <CheckCircle2 className="h-5 w-5 text-slate-450 dark:text-slate-400" />,
          },
        ].map((c) => (
          <div
            key={c.label}
            className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 flex items-start justify-between gap-3"
          >
            <div className="space-y-1.5 min-w-0">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                {c.label}
              </p>
              <p className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                {c.value}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                {c.description}
              </p>
            </div>
            <div className="h-10 w-10 shrink-0 flex items-center justify-center rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
              {c.icon}
            </div>
          </div>
        ))}
      </div>

      {/* Request payout card */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1.5 max-w-xl">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Coins className="h-5 w-5 text-brand-600 dark:text-brand-500" />
              {hasEligible ? 'Payout Available' : 'Payout Details'}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed font-semibold">
              {hasEligible
                ? `You have ${formatCurrency(eligibleTotal, payoutCurrency)} ready for withdrawal. Requesting a payout will transfer this balance to your bank account on file.`
                : 'Your earnings are held pending class completion and review. They are automatically moved to your "Available for Payout" balance 24 hours after each completed session.'}
            </p>
          </div>

          <div className="flex flex-col gap-2 shrink-0">
            <button
              onClick={() => void handleRequestPayout()}
              disabled={!hasEligible || payoutBusy}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 text-sm font-medium shadow-sm transition-colors disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
            >
              {payoutBusy ? (
                <span>Requesting…</span>
              ) : (
                <>
                  <span>Request Payout</span>
                  <ArrowUpRight className="h-4 w-4" />
                </>
              )}
            </button>
            {!hasEligible && !payoutBusy && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">No eligible earnings yet.</p>
            )}
            {payoutError && (
              <p className="text-right text-xs font-semibold text-red-650 dark:text-red-400 mt-1 max-w-[200px]">
                {payoutError}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Earnings ledger */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <FileText className="h-5 w-5 text-brand-600 dark:text-brand-500" />
          <span>Ledger</span>
        </h2>
        {earnings.length === 0 ? (
          <div className="flex flex-col justify-center items-center rounded-lg border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/25 p-10 text-center space-y-2">
            <Coins className="mx-auto h-8 w-8 text-slate-400 dark:text-slate-500" />
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">No earnings logged yet.</p>
            <p className="text-xs text-slate-450 dark:text-slate-500">Your session completions will record here automatically.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <table className="min-w-full text-xs font-semibold text-slate-600 dark:text-slate-300">
              <thead className="bg-slate-50 border-b border-slate-100 dark:border-slate-700 text-[10px] uppercase tracking-wider text-slate-505 dark:bg-slate-900/40 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-3 text-left">Date</th>
                  <th className="px-5 py-3 text-right">Gross</th>
                  <th className="px-5 py-3 text-right">Commission</th>
                  <th className="px-5 py-3 text-right">Net</th>
                  <th className="px-5 py-3 text-center">Status</th>
                  <th className="px-5 py-3 text-left">Eligible at</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {earnings.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-900/20 transition-colors">
                    <td className="px-5 py-3.5 text-slate-500 dark:text-slate-400">
                      {formatDate(e.createdAt)}
                    </td>
                    <td className="px-5 py-3.5 text-right text-slate-700 dark:text-slate-300 font-medium">
                      {formatCurrency(e.grossMinor, e.currency)}
                    </td>
                    <td className="px-5 py-3.5 text-right text-slate-450 dark:text-slate-500">
                      {formatCurrency(e.commissionMinor, e.currency)}
                    </td>
                    <td className="px-5 py-3.5 text-right font-extrabold text-slate-900 dark:text-slate-100">
                      {formatCurrency(e.netMinor, e.currency)}
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide border ${EARNING_COLORS[e.status] ?? ''}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${
                          e.status === 'eligible'
                            ? 'bg-emerald-500 animate-pulse'
                            : e.status === 'held'
                            ? 'bg-amber-500'
                            : 'bg-slate-400'
                        }`} />
                        {e.status}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-450 dark:text-slate-500">
                      {formatDate(e.eligibleAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Payout history */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <History className="h-5 w-5 text-brand-600 dark:text-brand-500" />
          Payout History
        </h2>
        {payouts.length === 0 ? (
          <div className="flex flex-col justify-center items-center rounded-lg border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/25 p-10 text-center space-y-2">
            <History className="mx-auto h-8 w-8 text-slate-400 dark:text-slate-500" />
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">No payout requests yet.</p>
            <p className="text-xs text-slate-455 dark:text-slate-500">Once you request a payout, status logs will appear here.</p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {payouts.map((p) => {
              const statusBorder =
                p.status === 'approved'
                  ? 'border-l-emerald-500 dark:border-l-emerald-600'
                  : p.status === 'pending'
                  ? 'border-l-amber-500 dark:border-l-amber-600'
                  : 'border-l-red-500 dark:border-l-red-600';
              return (
                <li
                  key={p.id}
                  className={`relative overflow-hidden rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 border-l-4 ${statusBorder}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-1 min-w-0">
                      <p className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">
                        {formatCurrency(p.totalNetMinor, p.currency)}
                      </p>
                      <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                        Requested {formatDate(p.createdAt)}
                      </p>
                      {p.adminNotes && (
                        <p className="mt-1.5 rounded bg-slate-50 dark:bg-slate-900 p-2 text-xs text-slate-600 dark:text-slate-400 border border-slate-100 dark:border-slate-800 leading-relaxed">
                          <span className="font-bold text-slate-800 dark:text-slate-200 block text-[11px] mb-0.5">Note:</span>
                          {p.adminNotes}
                        </p>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide border shrink-0 ${PAYOUT_COLORS[p.status] ?? ''}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${
                        p.status === 'approved'
                          ? 'bg-emerald-500'
                          : p.status === 'pending'
                          ? 'bg-amber-500'
                          : 'bg-red-500'
                      }`} />
                      {p.status}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
